import React, { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ContractDeviationType } from "@/types";
import {
  activateContractDeviationType,
  deactivateContractDeviationType,
  useGetContractDeviationTypes,
} from "@/services/ContractDeviations";
import Group from "@/components/atoms/Group";
import Text from "@/components/atoms/Text";
import Space from "@/components/atoms/Space";
import { SettingsFormContent } from "@/components/pages/Settings";
import { ColumnDef } from "@tanstack/react-table";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import CTA from "@/components/atoms/CTA";
import SettingsTable from "../../molecules/SettingsTable/SettingsTable";
import { notifications } from "@mantine/notifications";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";

interface DeviationsTableProps {
  isAdmin: boolean;
  companyId: number | undefined;
  setSelectedContractDeviation: React.Dispatch<React.SetStateAction<ContractDeviationType | null>>;
}

const DeviationsTable: FC<DeviationsTableProps> = ({ isAdmin, setSelectedContractDeviation, companyId }) => {
  const [showDisabled, setShowDisabled] = useState<boolean>(false);
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const { data: contractDeviations } = useGetContractDeviationTypes(companyId as number, showDisabled, {
    enabled: !!companyId,
  });

  const { mutateAsync: mutateSetDeviationActive } = useMutation({
    mutationFn: async ({ deviationId, active }: { deviationId: number; active: boolean }) => {
      await (active ? activateContractDeviationType(deviationId) : deactivateContractDeviationType(deviationId));
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["contractDeviationTypes", companyId],
      });
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("notifications:generic.updateSuccess"))
      );
    },
    onError: (_) => {
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.error"), t("notifications:generic.updateError"))
      );
    },
  });

  const columns: ColumnDef<ContractDeviationType>[] = useMemo(
    () => [
      {
        header: t("common:name"),
        accessorKey: "name",
      },
      {
        header: t("common:price"),
        accessorKey: "price",
        cell: ({ row }) => {
          return <span>{row.original.price + " " + row.original.currency}</span>;
        },
      },
      {
        header: t("settings:paymentFrequency"),
        accessorKey: "paymentInterval",
        cell: ({ row }) => {
          const interval = t(`enumerables:deviationPaymentInvervalById.${row.original.priceUnit}`);
          return <span>{interval}</span>;
        },
      },
      {
        header: t("common:description"),
        accessorKey: "description",
      },
    ],
    [t, contractDeviations]
  );

  // Deactivated types stay (old orders refer to them) and can be activated again.
  const editActions = (contractDeviation: ContractDeviationType) =>
    contractDeviation.disabled
      ? [
          {
            label: t("actions:activate"),
            action: async () => {
              await mutateSetDeviationActive({ deviationId: contractDeviation.id, active: true });
            },
          },
        ]
      : [
          {
            label: t("actions:edit"),
            action: () => {
              setSelectedContractDeviation(contractDeviation);
            },
          },
          {
            label: t("common:deactivate"),
            action: async () => {
              await mutateSetDeviationActive({ deviationId: contractDeviation.id, active: false });
            },
          },
        ];

  return (
    <React.Fragment>
      <SettingsFormContent>
        <Group justify="space-between">
          <Text size="md" strong={true} display="technical" tt="uppercase">
            {t("settings:deviationSettings.deviationList")}
          </Text>
          <CTA
            type="button"
            intent="secondary"
            onClick={async () => {
              setShowDisabled(!showDisabled);
              await queryClient.invalidateQueries({
                queryKey: ["contractDeviationTypes", companyId],
              });
            }}
          >
            {showDisabled ? t("actions:hideDeactivated") : t("actions:showDeactivated")}
          </CTA>
        </Group>
        <Space height="25px" />
        <SettingsTable
          data={contractDeviations || []}
          columns={columns}
          editActions={editActions}
          isAdmin={isAdmin}
          isRowInactive={(deviation) => deviation.disabled}
        />
      </SettingsFormContent>
    </React.Fragment>
  );
};

export default DeviationsTable;
